import React, { useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';
import { Helmet } from 'react-helmet';

import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';

import { allRegions } from '~/consts/allRegions';

import { useCreateRegionChannelSetting } from '~/hooks/apis/promo/useCreateRegionChannelSetting';
import { useDeleteRegionChannelSetting } from '~/hooks/apis/promo/useDeleteRegionChannelSetting';
import { useListRegionChannelSettings } from '~/hooks/apis/promo/useListRegionChannelSettings';
import { useUpdateRegionChannelSetting } from '~/hooks/apis/promo/useUpdateRegionChannelSetting';

import type {
  RegionChannelSetting,
  RegionChannelSettingPayload,
  RegionChannelSettingType,
} from '~/services/PromoService';

type SettingColumn = { key: string; label: string };
type FormField = { key: string; label: string; helper?: string };

/**
 * Registry of setting types. Each type declares how its rows render (columns) and how its
 * create/edit form is built + mapped to a payload. Adding a new setting type is a new entry
 * here (plus the matching svc-promo `setting_type`) — the page shell is type-agnostic.
 */
type SettingTypeConfig = {
  type: RegionChannelSettingType;
  label: string;
  columns: Array<SettingColumn>;
  getCell: (setting: RegionChannelSetting, key: string) => string | null;
  formFields: Array<FormField>;
  toPayload: (form: Record<string, string>, regionCode: string | null) => RegionChannelSettingPayload;
  initialForm: (setting?: RegionChannelSetting) => Record<string, string>;
};

const SETTING_TYPES: Array<SettingTypeConfig> = [
  {
    type: 'free_preview_auto_enrol',
    label: 'Free Preview auto-enrolment',
    columns: [
      { key: 'source', label: 'Source' },
      { key: 'medium', label: 'Medium' },
      { key: 'campaign', label: 'Campaign' },
      { key: 'term', label: 'Term' },
    ],
    getCell: (setting, key) => setting[key as 'source' | 'medium' | 'campaign' | 'term'] ?? null,
    formFields: [
      { key: 'source', label: 'utm_source' },
      { key: 'medium', label: 'utm_medium' },
      { key: 'campaign', label: 'utm_campaign' },
      { key: 'term', label: 'utm_term' },
    ],
    toPayload: (form, regionCode) => ({
      setting_type: 'free_preview_auto_enrol',
      region_code: regionCode ?? undefined,
      source: form.source?.trim() || undefined,
      medium: form.medium?.trim() || undefined,
      campaign: form.campaign?.trim() || undefined,
      term: form.term?.trim() || undefined,
    }),
    initialForm: (setting) => ({
      source: setting?.source ?? '',
      medium: setting?.medium ?? '',
      campaign: setting?.campaign ?? '',
      term: setting?.term ?? '',
    }),
  },
];

const configFor = (type: string) => SETTING_TYPES.find((t) => t.type === type);
const regionName = (code?: string | null) => allRegions.find((r) => r.code === code)?.name ?? code ?? 'Global';
const channelKey = (s: RegionChannelSetting) =>
  `${s.setting_type}|${s.source ?? ''}|${s.medium ?? ''}|${s.campaign ?? ''}|${s.term ?? ''}`;

// The datetime fields are shown to admins as UTC. A `datetime-local` input yields a naive
// 'YYYY-MM-DDTHH:mm' string, which we interpret as UTC and store as an ISO string (and back).
const toUtcIso = (localValue?: string): string | undefined => {
  if (!localValue) return undefined;
  const withZ = localValue.length === 16 ? `${localValue}:00Z` : `${localValue}Z`;
  const d = new Date(withZ);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
};
const isoToLocalInput = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}T${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
};

const settingToPayload = (s: RegionChannelSetting): RegionChannelSettingPayload => ({
  setting_type: s.setting_type,
  region_code: s.region_code ?? undefined,
  source: s.source ?? undefined,
  medium: s.medium ?? undefined,
  campaign: s.campaign ?? undefined,
  term: s.term ?? undefined,
  enabled: s.enabled,
  start_datetime: s.start_datetime ?? undefined,
  end_datetime: s.end_datetime ?? undefined,
});

type ModalState = {
  type: RegionChannelSettingType;
  regionCode: string | null;
  editingId: string | null;
  // Preserved from the edited row so saving edits doesn't silently flip a disabled rule back on;
  // defaults to true for a brand-new rule.
  enabled: boolean;
  form: Record<string, string>;
};

type ConfirmState = {
  title: string;
  message: React.ReactNode;
  confirmLabel: string;
  confirmColor?: 'error' | 'primary';
  onConfirm: () => void;
} | null;

function RegionRulePage() {
  const { enqueueSnackbar } = useSnackbar();
  const { data, isLoading, isError } = useListRegionChannelSettings({
    query: { status: 'active', pageSize: 500 },
  });
  const createSetting = useCreateRegionChannelSetting();
  const updateSetting = useUpdateRegionChannelSetting();
  const deleteSetting = useDeleteRegionChannelSetting();

  const settings = useMemo(() => data?.result?.regionChannelSettingList ?? [], [data]);
  const globalSettings = useMemo(() => settings.filter((s) => !s.region_code), [settings]);
  const globalChannelKeys = useMemo(() => new Set(globalSettings.map(channelKey)), [globalSettings]);

  const settingsByRegion = useMemo(() => {
    const map = new Map<string, Array<RegionChannelSetting>>();
    for (const s of settings) {
      if (!s.region_code) continue;
      const list = map.get(s.region_code) ?? [];
      list.push(s);
      map.set(s.region_code, list);
    }
    return map;
  }, [settings]);

  const [addedRegions, setAddedRegions] = useState<Array<string>>([]);
  const visibleRegionCodes = useMemo(() => {
    const set = new Set<string>([...settingsByRegion.keys(), ...addedRegions]);
    return [...set].sort();
  }, [settingsByRegion, addedRegions]);

  const [modal, setModal] = useState<ModalState | null>(null);
  const [confirm, setConfirm] = useState<ConfirmState>(null);
  const busy = createSetting.isPending || updateSetting.isPending || deleteSetting.isPending;

  const openCreate = (type: RegionChannelSettingType, regionCode: string | null) => {
    const cfg = configFor(type);
    if (!cfg) return;
    setModal({
      type,
      regionCode,
      editingId: null,
      enabled: true,
      form: { ...cfg.initialForm(), start_datetime: '', end_datetime: '' },
    });
  };
  const openEdit = (setting: RegionChannelSetting) => {
    const cfg = configFor(setting.setting_type);
    if (!cfg) return;
    setModal({
      type: setting.setting_type,
      regionCode: setting.region_code ?? null,
      editingId: setting.region_channel_setting_id,
      enabled: setting.enabled,
      form: {
        ...cfg.initialForm(setting),
        start_datetime: isoToLocalInput(setting.start_datetime),
        end_datetime: isoToLocalInput(setting.end_datetime),
      },
    });
  };
  const closeModal = () => setModal(null);

  const handleSave = () => {
    if (!modal) return;
    const cfg = configFor(modal.type);
    if (!cfg) return;
    const start_datetime = toUtcIso(modal.form.start_datetime);
    const end_datetime = toUtcIso(modal.form.end_datetime);
    if (start_datetime && end_datetime && new Date(end_datetime) < new Date(start_datetime)) {
      enqueueSnackbar('End datetime must be after start datetime', { variant: 'error' });
      return;
    }
    const payload = {
      ...cfg.toPayload(modal.form, modal.regionCode),
      enabled: modal.enabled,
      start_datetime,
      end_datetime,
    };
    const onSuccess = () => {
      enqueueSnackbar('Setting saved', { variant: 'success' });
      closeModal();
    };
    const onError = () => enqueueSnackbar('Failed to save setting', { variant: 'error' });
    if (modal.editingId) {
      updateSetting.mutate({ id: modal.editingId, payload }, { onSuccess, onError });
    } else {
      createSetting.mutate(payload, { onSuccess, onError });
    }
  };

  const handleToggle = (setting: RegionChannelSetting) => {
    updateSetting.mutate(
      { id: setting.region_channel_setting_id, payload: { ...settingToPayload(setting), enabled: !setting.enabled } },
      { onError: () => enqueueSnackbar('Failed to update setting', { variant: 'error' }) },
    );
  };

  const handleDelete = (id: string) => {
    deleteSetting.mutate(id, {
      onSuccess: () => enqueueSnackbar('Setting removed', { variant: 'success' }),
      onError: () => enqueueSnackbar('Failed to remove setting', { variant: 'error' }),
    });
  };

  // Removes a whole region: expires every setting scoped to it, then drops the tile. Waits for all
  // deletions to settle before reporting, so we don't show success while some are still in flight
  // (or have failed and will reappear on the next refetch).
  const handleDeleteRegion = async (code: string) => {
    const rows = settingsByRegion.get(code) ?? [];
    const results = await Promise.allSettled(rows.map((r) => deleteSetting.mutateAsync(r.region_channel_setting_id)));
    setAddedRegions((prev) => prev.filter((c) => c !== code));
    const failures = results.filter((r) => r.status === 'rejected').length;
    if (failures > 0) {
      enqueueSnackbar(`Failed to remove ${failures} setting(s) in ${regionName(code)}`, { variant: 'error' });
    } else {
      enqueueSnackbar(`Removed ${regionName(code)}`, { variant: 'success' });
    }
  };

  const renderManagedTable = (cfg: SettingTypeConfig, rows: Array<RegionChannelSetting>) => (
    <Table size="small">
      <TableHead>
        <TableRow>
          {cfg.columns.map((c) => (
            <TableCell key={c.key}>{c.label}</TableCell>
          ))}
          <TableCell>Status</TableCell>
          <TableCell align="right">Actions</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((s) => (
          <TableRow key={s.region_channel_setting_id}>
            {cfg.columns.map((c) => {
              const value = cfg.getCell(s, c.key);
              return <TableCell key={c.key}>{value || <em style={{ opacity: 0.6 }}>any</em>}</TableCell>;
            })}
            <TableCell>
              <Chip size="small" color={s.enabled ? 'success' : 'default'} label={s.enabled ? 'Enabled' : 'Disabled'} />
            </TableCell>
            <TableCell align="right">
              <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                <Tooltip title={s.enabled ? 'Disable' : 'Enable'}>
                  <Switch size="small" checked={s.enabled} disabled={busy} onChange={() => handleToggle(s)} />
                </Tooltip>
                <IconButton size="small" aria-label="edit" disabled={busy} onClick={() => openEdit(s)}>
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  aria-label="expire"
                  disabled={busy}
                  onClick={() =>
                    setConfirm({
                      title: 'Delete setting',
                      message: (
                        <Box>
                          <Typography variant="body2">Are you sure you want to delete this setting?</Typography>
                          <Typography variant="subtitle2" sx={{ mt: 1.5 }}>
                            {cfg.label}
                          </Typography>
                          <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
                            {cfg.columns.map((c) => (
                              <Typography component="li" variant="body2" key={c.key}>
                                {c.label.toLowerCase()}: {cfg.getCell(s, c.key) || 'any'}
                              </Typography>
                            ))}
                          </Box>
                        </Box>
                      ),
                      confirmLabel: 'Delete',
                      confirmColor: 'error',
                      onConfirm: () => handleDelete(s.region_channel_setting_id),
                    })
                  }
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Stack>
            </TableCell>
          </TableRow>
        ))}
        {rows.length === 0 && (
          <TableRow>
            <TableCell colSpan={cfg.columns.length + 2}>
              <Typography variant="body2" color="text.secondary">
                No settings configured.
              </Typography>
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );

  const renderInheritedTable = (
    cfg: SettingTypeConfig,
    regionCode: string,
    regionRows: Array<RegionChannelSetting>,
  ) => {
    const globalsOfType = globalSettings.filter((s) => s.setting_type === cfg.type);
    if (globalsOfType.length === 0) return null;
    return (
      <Table size="small">
        <TableHead>
          <TableRow>
            {cfg.columns.map((c) => (
              <TableCell key={c.key}>{c.label}</TableCell>
            ))}
            <TableCell align="right">This region</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {globalsOfType.map((g) => {
            const shadow = regionRows.find((r) => channelKey(r) === channelKey(g) && !r.enabled);
            const mode = shadow ? 'disable' : 'inherit';
            const onModeChange = (_e: React.MouseEvent<HTMLElement>, next: string | null) => {
              if (!next || next === mode) return;
              if (next === 'disable') {
                createSetting.mutate(
                  {
                    setting_type: g.setting_type,
                    region_code: regionCode,
                    source: g.source ?? undefined,
                    medium: g.medium ?? undefined,
                    campaign: g.campaign ?? undefined,
                    term: g.term ?? undefined,
                    enabled: false,
                  },
                  { onError: () => enqueueSnackbar('Failed to update region', { variant: 'error' }) },
                );
              } else if (shadow) {
                handleDelete(shadow.region_channel_setting_id);
              }
            };
            return (
              <TableRow key={g.region_channel_setting_id}>
                {cfg.columns.map((c) => {
                  const value = cfg.getCell(g, c.key);
                  return <TableCell key={c.key}>{value || <em style={{ opacity: 0.6 }}>any</em>}</TableCell>;
                })}
                <TableCell align="right">
                  <ToggleButtonGroup size="small" exclusive value={mode} onChange={onModeChange} disabled={busy}>
                    <ToggleButton value="inherit">Inherit</ToggleButton>
                    <ToggleButton value="disable">Disable</ToggleButton>
                  </ToggleButtonGroup>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    );
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title>Marketing / Region / Channel Control</title>
      </Helmet>

      <Container maxWidth="xl" sx={{ mt: 2 }}>
        <PageHeader title="Region Rule" />

        <Stack spacing={3} sx={{ p: 2 }}>
          {isError && <Alert severity="error">Failed to load settings.</Alert>}
          {/* Global settings */}
          <Divider textAlign="left" />
          <Typography variant="h3">Global Settings</Typography>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Applies to every region unless a region below overrides it.
            </Typography>
            <Stack spacing={2}>
              {SETTING_TYPES.map((cfg) => {
                const rows = globalSettings.filter((s) => s.setting_type === cfg.type);
                if (rows.length === 0) return null;
                return (
                  <Box key={cfg.type} sx={{ border: '0.5px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      {cfg.label}
                    </Typography>
                    {renderManagedTable(cfg, rows)}
                  </Box>
                );
              })}
            </Stack>
            <Box sx={{ mt: 2 }}>
              <AddSettingDropdown onAdd={(type) => openCreate(type, null)} disabled={busy} />
            </Box>
          </Paper>

          <Divider textAlign="left" />
          <Typography variant="h3">Regions Settings</Typography>

          <AddRegionSelector
            existingRegionCodes={visibleRegionCodes}
            onAddRegion={(code) =>
              setConfirm({
                title: 'Add region',
                message: (
                  <Typography variant="body2">
                    Add {regionName(code)} ({code}) to region rules?
                  </Typography>
                ),
                confirmLabel: 'Add',
                confirmColor: 'primary',
                onConfirm: () => setAddedRegions((prev) => [...prev, code]),
              })
            }
          />

          {visibleRegionCodes.map((code) => {
            const regionRows = settingsByRegion.get(code) ?? [];
            return (
              <Paper key={code} variant="outlined" sx={{ p: 2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                  <Typography variant="h6">
                    {regionName(code)} ({code})
                  </Typography>
                  <Tooltip title="Remove region">
                    <IconButton
                      aria-label="remove region"
                      disabled={busy}
                      onClick={() =>
                        setConfirm({
                          title: 'Delete region',
                          message: (
                            <Typography variant="body2">
                              Are you sure you want to delete {regionName(code)} ({code})? This removes{' '}
                              {regionRows.length} region setting(s).
                            </Typography>
                          ),
                          confirmLabel: 'Delete',
                          confirmColor: 'error',
                          onConfirm: () => void handleDeleteRegion(code),
                        })
                      }
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>

                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Inherited from global
                </Typography>
                <Stack spacing={2} sx={{ mb: 2 }}>
                  {SETTING_TYPES.map((cfg) => {
                    const table = renderInheritedTable(cfg, code, regionRows);
                    if (!table) return null;
                    return (
                      <Box
                        key={cfg.type}
                        sx={{ border: '0.5px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}
                      >
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          {cfg.label}
                        </Typography>
                        {table}
                      </Box>
                    );
                  })}
                </Stack>

                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Region-specific settings
                </Typography>
                <Stack spacing={2}>
                  {SETTING_TYPES.map((cfg) => {
                    // Region-specific rows exclude "disable" shadows of a global (those show in the
                    // Inherited section as the Disable state).
                    const rows = regionRows.filter(
                      (s) => s.setting_type === cfg.type && !(!s.enabled && globalChannelKeys.has(channelKey(s))),
                    );
                    if (rows.length === 0) return null;
                    return (
                      <Box
                        key={cfg.type}
                        sx={{ border: '0.5px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}
                      >
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          {cfg.label}
                        </Typography>
                        {renderManagedTable(cfg, rows)}
                      </Box>
                    );
                  })}
                </Stack>
                <Box sx={{ mt: 2 }}>
                  <AddSettingDropdown onAdd={(type) => openCreate(type, code)} disabled={busy} />
                </Box>
              </Paper>
            );
          })}

          {/* Create / edit modal */}
          <Dialog open={!!modal} onClose={closeModal} fullWidth maxWidth="sm">
            {modal &&
              (() => {
                const cfg = configFor(modal.type);
                if (!cfg) return null;
                return (
                  <>
                    <DialogTitle>
                      {modal.editingId ? 'Edit' : 'Add'} — {cfg.label}
                      {modal.regionCode ? ` (${regionName(modal.regionCode)})` : ' (Global)'}
                    </DialogTitle>
                    <DialogContent>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Leave the channel fields blank to apply to all traffic. Times are UTC — leave a datetime blank
                        for no bound (both blank = long term).
                      </Typography>
                      <Stack spacing={2} sx={{ mt: 1 }}>
                        {cfg.formFields.map((f) => (
                          <TextField
                            key={f.key}
                            label={f.label}
                            helperText={f.helper}
                            value={modal.form[f.key] ?? ''}
                            onChange={(e) =>
                              setModal((m) => (m ? { ...m, form: { ...m.form, [f.key]: e.target.value } } : m))
                            }
                            fullWidth
                          />
                        ))}
                        <TextField
                          type="datetime-local"
                          label="Start datetime (UTC)"
                          InputLabelProps={{ shrink: true }}
                          helperText="Blank = no start bound (active from now)."
                          value={modal.form.start_datetime ?? ''}
                          onChange={(e) =>
                            setModal((m) => (m ? { ...m, form: { ...m.form, start_datetime: e.target.value } } : m))
                          }
                          fullWidth
                        />
                        <TextField
                          type="datetime-local"
                          label="End datetime (UTC)"
                          InputLabelProps={{ shrink: true }}
                          helperText="Blank = no end bound."
                          value={modal.form.end_datetime ?? ''}
                          onChange={(e) =>
                            setModal((m) => (m ? { ...m, form: { ...m.form, end_datetime: e.target.value } } : m))
                          }
                          fullWidth
                        />
                      </Stack>
                    </DialogContent>
                    <DialogActions>
                      <Button onClick={closeModal} disabled={busy}>
                        Cancel
                      </Button>
                      <Button variant="contained" onClick={handleSave} disabled={busy}>
                        Save
                      </Button>
                    </DialogActions>
                  </>
                );
              })()}
          </Dialog>

          {/* Confirmation dialog — row delete, region delete, and add region */}
          <Dialog open={!!confirm} onClose={() => setConfirm(null)} maxWidth="xs" fullWidth>
            <DialogTitle>{confirm?.title}</DialogTitle>
            <DialogContent>{confirm?.message}</DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirm(null)}>Cancel</Button>
              <Button
                variant="contained"
                color={confirm?.confirmColor ?? 'primary'}
                onClick={() => {
                  confirm?.onConfirm();
                  setConfirm(null);
                }}
              >
                {confirm?.confirmLabel ?? 'Confirm'}
              </Button>
            </DialogActions>
          </Dialog>
        </Stack>
      </Container>
    </>
  );
}

function AddSettingDropdown({
  onAdd,
  disabled,
}: {
  onAdd: (type: RegionChannelSettingType) => void;
  disabled?: boolean;
}) {
  const [value, setValue] = useState('');
  return (
    <FormControl size="small" sx={{ minWidth: 260 }}>
      <InputLabel id="add-setting">Add new setting</InputLabel>
      <Select
        labelId="add-setting"
        label="Add new setting"
        value={value}
        disabled={disabled}
        onChange={(e: SelectChangeEvent) => {
          const type = e.target.value as RegionChannelSettingType;
          if (type) onAdd(type);
          setValue('');
        }}
      >
        {SETTING_TYPES.map((cfg) => (
          <MenuItem key={cfg.type} value={cfg.type}>
            {cfg.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

function AddRegionSelector({
  existingRegionCodes,
  onAddRegion,
}: {
  existingRegionCodes: Array<string>;
  onAddRegion: (code: string) => void;
}) {
  const [value, setValue] = useState('');
  const available = allRegions.filter((r) => !existingRegionCodes.includes(r.code));
  return (
    <FormControl size="small" sx={{ minWidth: 260 }}>
      <InputLabel id="add-region">Add region</InputLabel>
      <Select
        labelId="add-region"
        label="Add region"
        value={value}
        onChange={(e: SelectChangeEvent) => {
          if (e.target.value) onAddRegion(e.target.value);
          setValue('');
        }}
      >
        {available.map((r) => (
          <MenuItem key={r.code} value={r.code}>
            {r.name} ({r.code})
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export default RegionRulePage;
